export type Priority = {
	/**
	 * Status color for priority in HEX color code
	 */
	statusColor: string;
	/**
	 * Can be empty string, UGC
	 */
	description?: string;
	/**
	 * ID of the avatar associated with the priority
	 * If undefined, the priority is being set with an iconURL and is not
	 * using the universal avatar APIs
	 */
	avatarId?: string;
	/**
	 * Absolute URL
	 */
	iconUrl: string;
	/**
	 * Cannot be empty string, UGC
	 */
	name: string;
	/**
	 * Arbitrary, safe and unique ID
	 */
	id: string;
	/**
	 * Whether this priority is the default for the Jira instance
	 */
	isDefault: boolean;
	/**
	 * The priority schemes containing this priority
	 * Will return a maximum of 15 schemes in the values response.
	 */
	schemes: AssociatedPrioritySchemesPerPriority;
};

export type AssociatedPrioritySchemesPerPriority = {
	isLast: boolean;
	maxResults: number;
	startAt: number;
	total: number;
	values: ExpandResponsePriorityScheme[];
};

export type PriorityAvatar = {
	/**
	 * ID of the avatar associated with the priority
	 */
	id: string;
	/**
	 * Absolute URL of the avatar
	 */
	dataURI: string;
};

/**
 * API
 */

// https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issue-priorities/#api-rest-api-3-priority-search-get
export type SearchPrioritiesAPIResponse = {
	isLast: boolean;
	maxResults: number;
	startAt: number;
	total: number;
	values: Array<Priority>;
};

export type RefinedFetchError = {
	/**
	 * HTTP status code, will not include 2XX success codes
	 */
	statusCode: number;
	traceId?: string | undefined;
	message?: string | undefined;
};

export type PaginatedPrioritiesData = {
	/**
	 * Dictionary of page number to priorities.
	 * pageNumber index starts from 1.
	 * */
	pages: Record<number, { items: Priority[]; isLast: boolean }>;
	/**
	 * Number of priorities to show per page
	 * */
	pageSize: number;
	/**
	 * Total number of priorities for the Jira instance
	 * */
	totalItems: number;
};

export type ExpandResponsePriorityScheme = {
	id: string;
	name: string;
	self: string;
};

/**
 * Priority Table
 */

export type PrioritiesTablePage = {
	/**
	 * Priorities to show on the page
	 * */
	items: Array<Priority>;
	/**
	 * Current page number which starts from 1
	 * */
	pageNumber: number;
	/**
	 * Number of priorities to show per page
	 * */
	pageSize: number;
	/**
	 * Total number of priorities for the Jira instance
	 * */
	totalItems: number;
	/**
	 * Whether this is the last page
	 */
	isLast: boolean;
};

export const INITIAL = 'INITIAL';
export const ERROR = 'ERROR';
export const LOADING = 'LOADING';
export const SUCCESS = 'SUCCESS';

export type PrioritiesTableState = typeof INITIAL | typeof ERROR | typeof LOADING | typeof SUCCESS;

export type PriorityPageReorderAction = 'UP' | 'DOWN';

export type ReorderParams =
	| {
			priorityId: string;
			action: PriorityPageReorderAction;
	  }
	| {
			priorityId: string;
			action: 'DRAG';
			dragSourceIndex: number;
			dragDestinationIndex: number;
	  }
	| {
			priorityId: string;
			action: 'SET_POSITION';
			position: number; // The position to move to, starting from 1
	  };

export type ReorderHandler = (params: ReorderParams) => void;

/**
 * Represents the request body params to be used in the move priorities API.
 */
export type MovePrioritiesRequestParams = {
	ids: string[]; // The IDs of the priorities to be moved
} & (
	| { position: 'First' | 'Last' } // Move the priorities to the beginning or end
	| { after: string } // Move the priorities after the specified priority ID
);

export type GetAvatarsPathParams = {
	type: 'priority';
	/**
	 * Represents the accountId of the user.
	 */
	entityId: string;
};

export type PostAvatarPathParams = {
	type: 'priority';
	/**
	 * In our use case, represents the userId/owner of these avatars.
	 */
	entityId: string;
};

export type AvatarResponseObject = {
	id: string;
	isDeletable: boolean;
	isSelected: boolean;
	isSystemAvatar: boolean;
	urls: {
		'16x16': string;
		'24x24': string;
		'32x32': string;
		'48x48': string;
	};
};

export type GetAvatarsResponse = {
	custom: AvatarResponseObject[];
	system: AvatarResponseObject[];
};

export type ImagePickedParams = {
	file: File;
	crop: CropProperties;
};

export type PostAvatarResponse = {
	id: string;
	isDeletable: boolean;
	isSelected: boolean;
	isSystemAvatar: boolean;
};

export type CropProperties = {
	x: number;
	y: number;
	size: number;
};

export type PriorityCurrentModalState = {
	modalName: 'DELETE' | 'DELETE_IN_USE' | 'MOVE';
	priority: Priority;
} | null;

export type ClosePriorityModal = () => void;

export type CreatePrioritySidebar = {
	sidebarName: 'CREATE';
	submitting?: boolean;
};

export type EditPrioritySidebar = {
	sidebarName: 'EDIT';
	priority: Priority;
	submitting?: boolean;
};

export type PriorityCurrentSidebarState = CreatePrioritySidebar | EditPrioritySidebar | null;

export type CloseSidebarModal = () => void;

export type CreateAndEditPriorityRequestBody = {
	name: string;
	description?: string;
	/**
	 * If avatarId is null, the priority icon is defined by iconUrl.
	 * The old method for handling priority icons. This will be deprecated in the future.
	 */
	avatarId?: string;
	statusColor: string;
};

export type CreatePriorityResponse = {
	id: string;
};

export type EditPriorityResponse = {};

export type PriorityFormAsyncErrors = {
	duplicateName?: string;
} | null;

export type DeletePriorityResponse = {
	self: string;
	status: string;
};
